import clsx from 'clsx'
import React from 'react'

import Label from './Label'

type TooltipPosition = 'right' | 'left' | 'top' | 'bottom'

interface TooltipProps extends React.HTMLAttributes<HTMLDivElement> {
    label: React.ReactNode
    position?: TooltipPosition
    disabled?: boolean
    wrap?: boolean
}

const getPositionClasses = (position: TooltipPosition): string => {
    switch (position) {
        case 'right':
            return 'left-full top-1/2 -translate-y-1/2 ml-3.5'
        case 'left':
            return 'right-full top-1/2 -translate-y-1/2 mr-3.5'
        case 'top':
            return 'bottom-full left-1/2 -translate-x-1/2 mb-3.5'
        case 'bottom':
            return 'top-full left-1/2 -translate-x-1/2 mt-3.5'
    }
}

const getArrowClasses = (position: TooltipPosition): string => {
    switch (position) {
        case 'right':
            return 'left-0 top-1/2 -translate-y-1/2 -translate-x-full border-r-gray-700'
        case 'left':
            return 'right-0 top-1/2 -translate-y-1/2 translate-x-full border-l-gray-700'
        case 'top':
            return 'bottom-0 left-1/2 -translate-x-1/2 translate-y-full border-t-gray-700'
        case 'bottom':
            return 'top-0 left-1/2 -translate-x-1/2 -translate-y-full border-b-gray-700'
    }
}

const Tooltip: React.FC<TooltipProps> = ({
    label,
    children,
    position = 'right',
    disabled = false,
    wrap = false,
    ...props
}) => {
    return (
        <div className="group relative inline-block" {...props}>
            {children}
            <div
                className={clsx(
                    'invisible absolute z-50 w-max max-w-[290px] rounded-lg bg-gray-700 p-3 text-sm text-white opacity-0 transition-opacity',
                    getPositionClasses(position),
                    { 'group-hover:visible group-hover:opacity-100': !disabled },
                    { 'group-hover:invisible group-hover:opacity-0': disabled }
                )}
                data-testid="tooltip-wrapper"
            >
                <div
                    className={clsx('w-full overflow-x-hidden text-ellipsis', {
                        'whitespace-break-spaces': wrap,
                        'whitespace-nowrap': !wrap,
                    })}
                >
                    <Label size="sm">{label}</Label>
                </div>
                <div
                    className={`absolute size-0 border-8 border-transparent ${getArrowClasses(position)}`}
                />
            </div>
        </div>
    )
}

export default Tooltip
